import { Faro, initializeFaro, SessionInstrumentation } from "@grafana/faro-web-sdk";
import { TelemetryInstance } from "./Telemetry";
import { sanitizeError } from "./sanitize";
import { sanitizeProperties, WriteKeyMap } from "./utils";
import { getEnvironmentConfig } from "~/modules/config";
import { ClientOptionsStore } from "~/modules/client/ClientOptions/ClientOptionsStore";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName, Logger } from "~/modules/logger";
import { getOrigin } from "~/utils/getOrigin";
import { parseRegionForAnalytics, RegionType } from "~/utils/regionUtil";
import { EventProperties } from "~/modules/telemetry/Analytics";
import packageInfo from "package.json";




export class TelemetryImpl implements TelemetryInstance {
    private readonly clientOptions: ClientOptionsStore;

    private disabled: boolean = false;

    private faro: Faro | undefined;

    private readonly logger: Logger;

    private readonly env: RegionType;

    private readonly commonProperties: EventProperties = {};

    constructor(ctx: ContextManager) {
        this.clientOptions = ctx.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        this.logger = getLogger(ctx)(LoggerName.Analytics);
        const environmentConfig = getEnvironmentConfig();
        const { region } = environmentConfig ?? {};
        const origin = getOrigin();
        const configRegion = region ?? this.clientOptions.region ?? "us1";
        this.env = parseRegionForAnalytics(configRegion);
        const flexUiVersion = origin === "flexui" && this.clientOptions.appVersion;
        this.commonProperties = {
            origin,
            ...(flexUiVersion && { flexUiVersion })
        };
    }

    








    public init(telemetryDisabled: boolean, writeKeyMap?: WriteKeyMap, accountSid?: string): void {
        if (telemetryDisabled) {
            this.disabled = true;
            return;
        }

        if (this.faro) {
            this.logger.warn("FlexSDK Telemetry is already initialized. Moving on");
            return;
        }
        try {
            const telemetryConfig = writeKeyMap?.telemetry?.[this.env] ?? writeKeyMap?.telemetry?.prod ?? undefined;
            if (!telemetryConfig?.applicationId || !telemetryConfig?.clientToken) {
                this.logger.warn("FlexSDK Telemetry Key not found. The FlexSDK Telemetry will be disabled.");
                this.disabled = true;
                return;
            }

            this.faro = initializeFaro({
                url: `https://${telemetryConfig.applicationId}/collect/${telemetryConfig.clientToken}`,
                isolate: true,
                instrumentations: [new SessionInstrumentation()],
                app: {
                    name: "FlexSDK",
                    version: packageInfo.version,
                    environment: this.env,
                    namespace: "flexsdk"
                },
                user: accountSid
                    ? {
                          id: accountSid,
                          attributes: {
                              accountSid
                          }
                      }
                    : undefined,
                





                metas: []
            });
        } catch (error) {
            this.logger.error("Error initializing FlexSDK Telemetry. The telemetry will be disabled.", error);
            this.disabled = true;
        }
    }

    







    trackError(error: Error, context?: object): void {
        if (this.disabled || !this.faro) {
            return;
        }

        const sanitized = sanitizeError(error);
        const safeContext = sanitizeProperties({ ...(context as Record<string, unknown>), ...this.commonProperties });
        this.faro.api.pushError(sanitized, { context: safeContext as Record<string, string> });
    }

    







    trackUsage(eventName: string, properties?: object): void {
        if (this.disabled || !this.faro) {
            return;
        }
        const safeProps = sanitizeProperties({ ...(properties as Record<string, unknown>), ...this.commonProperties });
        this.faro.api.pushEvent(eventName, safeProps as Record<string, string>);
    }
}
